import { useLazyQuery } from "@apollo/client";
import { Dialog, Transition } from "@headlessui/react";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FETCH_INSTANCE_GALLERY } from "../../../../graphql/query/Instance";
import { FETCH_ITEMS_V2 as fetchItemsV2 } from "../../../../graphql/query/Item";
import {
  buildSearchResultNavigationSnapshot,
  getSearchResultNavigationQuery,
  getSearchResultNavigationState,
} from "../../../../util/searchResultNavigation";
import { instanceNavigationStore } from "../useInstanceFilters";
import { FETCH_INSTANCES_LIST as fetchInstancesList } from "../../../../graphql/query/Instance";
import {
  getNextImageIndex,
  getPreviousImageIndex,
} from "../instanceGalleryNavigation";
import useGalleryDarkMode from "./useGalleryDarkMode";
import ImagePane from "./ImagePane";
import ControlBar from "./ControlBar";
import Crossfade from "./Crossfade";
import DetailPane from "./DetailPane";

const FETCH_SALE_HISTORY = fetchItemsV2({
  invoice: {
    contact: { name: true, company: true },
    currency: { code: true },
  },
});

const InstanceGalleryModal = ({ instanceId, isOpen, onClose }) => {
  const [darkMode, toggleDarkMode] = useGalleryDarkMode();
  const [currentInstanceId, setCurrentInstanceId] = useState(instanceId);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [navigationSnapshot, setNavigationSnapshot] = useState(() =>
    instanceNavigationStore.get(),
  );

  const [fetchInstance, { data, loading }] = useLazyQuery(
    FETCH_INSTANCE_GALLERY,
    { fetchPolicy: "cache-first" },
  );

  const [fetchSaleHistory, { data: salesData }] = useLazyQuery(
    FETCH_SALE_HISTORY,
    { fetchPolicy: "cache-first" },
  );

  const [fetchNavigationPage] = useLazyQuery(fetchInstancesList, {
    fetchPolicy: "network-only",
  });

  const historyPushedRef = useRef(false);

  // gallery open state management
  useEffect(() => {
    if (instanceId && isOpen) {
      setCurrentInstanceId(instanceId);
      setCurrentImageIndex(0);
      setFullscreen(false);
      document.querySelectorAll("[data-image-zoom-element]").forEach(el => {
        el.style.display = "none";
      });
      window.history.pushState({ galleryOpen: true }, "");
      historyPushedRef.current = true;
    }
  }, [instanceId, isOpen]);

  // handle browser back button. Skip when the gallery was already closed
  // through the UI: closeGallery clears historyPushedRef before calling
  // history.back(), so this listener becomes a no-op for that case.
  useEffect(() => {
    if (!isOpen) return;
    const handlePopState = () => {
      if (!historyPushedRef.current) return;
      historyPushedRef.current = false;
      onClose(currentInstanceId);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isOpen, onClose, currentInstanceId]);

  // fetch instance and sale history when navigating to different instance
  useEffect(() => {
    if (currentInstanceId && isOpen) {
      fetchInstance({ variables: { id: currentInstanceId } });
      fetchSaleHistory({
        variables: {
          input: {
            instanceId: { value: currentInstanceId, operator: "eq" },
            typeId: { value: 8, operator: "eq" },
          },
        },
      });
    }
  }, [currentInstanceId, isOpen, fetchInstance, fetchSaleHistory]);

  // get the information about the current instance in the context of what the current search we have has.
  useEffect(() => {
    const unsubscribe = instanceNavigationStore.subscribe(
      setNavigationSnapshot,
    );
    return () => unsubscribe();
  }, []);

  // Close the gallery immediately via onClose; if we pushed a history entry
  // when opening, also pop it so the browser stack stays balanced. The
  // previous implementation relied solely on popstate firing in response to
  // history.back() which sometimes did not propagate, leaving the modal open.
  const closeGallery = useCallback(() => {
    const hadHistoryEntry = historyPushedRef.current;
    historyPushedRef.current = false;
    onClose(currentInstanceId);
    if (hadHistoryEntry) {
      window.history.back();
    }
  }, [onClose, currentInstanceId]);

  const instance = data?.instance;
  const images = useMemo(
    () =>
      [...(instance?.edition?.artwork?.images ?? [])].sort(
        (a, b) => (a.order ?? Infinity) - (b.order ?? Infinity),
      ),
    [instance?.edition?.artwork?.images],
  );
  const sales = salesData?.itemsV2?.edges ?? [];

  const navigationState = useMemo(
    () =>
      getSearchResultNavigationState({
        recordId: currentInstanceId,
        snapshot: navigationSnapshot,
      }),
    [currentInstanceId, navigationSnapshot],
  );

  const navigateToInstance = useCallback(
    async direction => {
      document.activeElement?.blur();
      const targetId =
        direction === "previous"
          ? navigationState.previousId
          : navigationState.nextId;

      if (targetId) {
        setCurrentInstanceId(targetId);
        setCurrentImageIndex(0);
        return;
      }

      const targetOffset =
        direction === "previous"
          ? navigationState.previousOffset
          : navigationState.nextOffset;

      if (targetOffset === null) return;

      const currentSnapshot = instanceNavigationStore.get();
      const queryInput = getSearchResultNavigationQuery(
        instanceNavigationStore,
        targetOffset,
      );

      const { data: pageData } = await fetchNavigationPage({
        variables: { input: queryInput },
      });

      const { instancesV2 } = pageData || {};
      if (instanceNavigationStore.get() !== currentSnapshot) return;
      const nextSnapshot = buildSearchResultNavigationSnapshot({
        count: instancesV2?.count || 0,
        edges: instancesV2?.edges || [],
        listContext: currentSnapshot?.listContext || {},
        queryVariables: queryInput,
        filters: instanceNavigationStore.filterStore.get(),
        searchSynopsisTerms: currentSnapshot?.searchSynopsisTerms,
        statusContext: currentSnapshot?.statusContext || "all",
      });
      instanceNavigationStore.set(nextSnapshot);

      const newPageIds = nextSnapshot.pageIds || [];
      const newId =
        direction === "previous"
          ? newPageIds[newPageIds.length - 1]
          : newPageIds[0];

      if (newId) {
        setCurrentInstanceId(newId);
        setCurrentImageIndex(0);
      }
    },
    [navigationState, fetchNavigationPage],
  );

  const handlePreviousImage = useCallback(() => {
    setCurrentImageIndex(prev => getPreviousImageIndex(prev, images.length));
  }, [images.length]);

  const handleNextImage = useCallback(() => {
    setCurrentImageIndex(prev => getNextImageIndex(prev, images.length));
  }, [images.length]);

  const handleThumbnailSelect = useCallback(index => {
    setCurrentImageIndex(index);
  }, []);

  const toggleFullscreen = useCallback(() => {
    setFullscreen(prev => !prev);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = event => {
      if (event.defaultPrevented || event.repeat) return;

      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (fullscreen) {
          setFullscreen(false);
        } else {
          closeGallery();
        }
        return;
      }

      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          handlePreviousImage();
          break;
        case "ArrowRight":
          event.preventDefault();
          handleNextImage();
          break;
        case "ArrowUp":
          event.preventDefault();
          if (navigationState.hasPrevious) navigateToInstance("previous");
          break;
        case "ArrowDown":
          event.preventDefault();
          if (navigationState.hasNext) navigateToInstance("next");
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [
    isOpen,
    fullscreen,
    handlePreviousImage,
    handleNextImage,
    navigationState,
    navigateToInstance,
    closeGallery,
  ]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[100] overflow-hidden"
        onClose={closeGallery}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-80"
          leave="ease-in duration-200"
          leaveFrom="opacity-80"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-gray-300" />
        </Transition.Child>

        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div
            className={`fixed inset-0 flex flex-col transition-colors duration-300 ${darkMode ? "bg-neutral-950" : "bg-white"}`}
          >
            {/* Main content: stacked in portrait, side-by-side in landscape */}
            <div className="relative flex flex-1 flex-col overflow-hidden landscape:flex-row">
              {!fullscreen && (
                <ControlBar
                  instance={instance}
                  images={images}
                  navigationState={navigationState}
                  loading={loading}
                  onNavigate={navigateToInstance}
                  onClose={closeGallery}
                  darkMode={darkMode}
                  onToggleDarkMode={toggleDarkMode}
                  currentImageIndex={currentImageIndex}
                />
              )}
              <div
                className={
                  fullscreen
                    ? "h-full w-full select-none"
                    : `min-h-0 shrink basis-[60%] select-none border-b landscape:h-full landscape:w-[60%] landscape:shrink-0 landscape:basis-auto landscape:border-b-0 landscape:border-r ${darkMode ? "border-gray-800" : "border-gray-200"}`
                }
              >
                <Crossfade
                  transitionKey={currentInstanceId}
                  className="h-full w-full"
                >
                  <ImagePane
                    images={images}
                    currentIndex={currentImageIndex}
                    onPrevious={handlePreviousImage}
                    onNext={handleNextImage}
                    onThumbnailSelect={handleThumbnailSelect}
                    loading={loading}
                    darkMode={darkMode}
                    onSwipeUp={() => navigateToInstance("next")}
                    onSwipeDown={() => navigateToInstance("previous")}
                    fullscreen={fullscreen}
                    onToggleFullscreen={toggleFullscreen}
                  />
                </Crossfade>
              </div>
              {!fullscreen && (
                <div className="min-h-0 shrink basis-[40%] landscape:h-full landscape:w-[40%] landscape:shrink-0 landscape:basis-auto">
                  <Crossfade
                    transitionKey={currentInstanceId}
                    className="h-full w-full"
                  >
                    <DetailPane
                      instance={instance}
                      sales={sales}
                      loading={loading}
                      darkMode={darkMode}
                    />
                  </Crossfade>
                </div>
              )}
            </div>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};

export default InstanceGalleryModal;
